import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { AuthContext } from "../../context/AuthProvider";
import { provisionLine } from "../../lib/api/v2Api";
import { device } from "../../lib/breakpoints";
import { APP_NAME } from "../../helpers/constants";

const V2_ADDRESS_KEY = "lo_v2_address";

const STATES = {
  LOADING: "loading",
  LIVE: "live",
  ERROR: "error",
};

export default function V2SimulationScreen() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [state, setState] = useState(STATES.LOADING);
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!user) return;

    const storedAddress = sessionStorage.getItem(V2_ADDRESS_KEY) || "";
    setAddress(storedAddress);

    let cancelled = false;

    provisionLine(user, storedAddress)
      .then((result) => {
        if (cancelled) return;
        setPhone(result.formattedPhone || result.phoneNumber);
        sessionStorage.removeItem(V2_ADDRESS_KEY);
        setState(STATES.LIVE);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[V2] Provision error:", err);
        setErrorMsg(err.message || "Something went wrong. Please try again.");
        setState(STATES.ERROR);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  if (state === STATES.LOADING) {
    return (
      <Wrap>
        <LoadingCard>
          <Spinner aria-label="Provisioning your AI front desk" />
          <LoadingTitle>Provisioning your AI Front Desk</LoadingTitle>
          <LoadingSteps>
            <Step delay="0s">Linking your property</Step>
            <Step delay="1.2s">Turning on Clumse AI triage</Step>
            <Step delay="2.4s">Running final checks</Step>
          </LoadingSteps>
        </LoadingCard>
      </Wrap>
    );
  }

  if (state === STATES.ERROR) {
    return (
      <Wrap>
        <ErrorCard>
          <ErrorIcon aria-hidden="true">!</ErrorIcon>
          <ErrorTitle>Provisioning failed</ErrorTitle>
          <ErrorBody>{errorMsg}</ErrorBody>
          <RetryButton onClick={() => window.location.reload()}>
            Try Again
          </RetryButton>
        </ErrorCard>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Card>
        <LiveBadge>
          <PulseDot aria-hidden="true" />
          LIVE
        </LiveBadge>

        <Headline>Your AI front desk is live.</Headline>

        <ReadyScript>
          {address ? (
            <>
              Clumse AI is set up at <AddressHighlight>{address}</AddressHighlight>.
              Open your triage dashboard and run a test thread to mirror the
              occupant-facing experience.
            </>
          ) : (
            <>
              Clumse AI is set up for your property. Open your triage dashboard and
              run a test thread to mirror the occupant-facing experience.
            </>
          )}
        </ReadyScript>

        {phone ? (
          <PhoneMeta aria-label={`Provisioned line: ${phone}`}>
            Connected line <PhoneInline>{phone}</PhoneInline>
          </PhoneMeta>
        ) : null}

        <WhatHappens>
          <WhatHappensTitle>What happens in triage:</WhatHappensTitle>
          <StepList>
            <StepItem>
              <StepNumber>1</StepNumber>
              <StepText>
                Clumse AI replies in the thread as {APP_NAME}&apos;s front desk,
                usually within about 10 seconds.
              </StepText>
            </StepItem>
            <StepItem>
              <StepNumber>2</StepNumber>
              <StepText>
                It clarifies the issue, checks responsibility notes you saved, and
                decides if a vendor belongs in the loop.
              </StepText>
            </StepItem>
            <StepItem>
              <StepNumber>3</StepNumber>
              <StepText>
                A verified transcript lands in your Triage Dashboard. Your
                personal phone stays quiet for routine messages.
              </StepText>
            </StepItem>
          </StepList>
        </WhatHappens>

        <Actions>
          <PrimaryAction onClick={() => navigate("/v2/dashboard")}>
            View Your Triage Dashboard
          </PrimaryAction>
          <SecondaryAction onClick={() => navigate("/profile")}>
            Configure Emergency Routing
          </SecondaryAction>
        </Actions>
      </Card>
    </Wrap>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const dotPulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.85); }
`;

const fadeStep = keyframes`
  0%   { opacity: 0; transform: translateX(-8px); }
  20%  { opacity: 1; transform: translateX(0); }
  80%  { opacity: 1; transform: translateX(0); }
  100% { opacity: 0.4; transform: translateX(0); }
`;

const Wrap = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0f0a 0%, #0d1f0e 50%, #0a1510 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.25rem;
`;

const Card = styled.div`
  max-width: 600px;
  width: 100%;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 20px;
  padding: 3rem 2.5rem;
  text-align: center;
  animation: ${fadeIn} 0.5s ease;
  @media ${device.mobile} {
    padding: 2rem 1.5rem;
  }
`;

const LiveBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  color: #4ade80;
  background: rgba(74, 222, 128, 0.1);
  border: 1px solid rgba(74, 222, 128, 0.3);
  border-radius: 100px;
  padding: 0.3rem 0.9rem;
  margin-bottom: 1.5rem;
`;

const PulseDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ade80;
  animation: ${dotPulse} 1.4s ease-in-out infinite;
`;

const Headline = styled.h1`
  font-size: clamp(1.75rem, 5vw, 2.5rem);
  font-weight: 700;
  color: #f0fdf4;
  margin: 0 0 1.25rem 0;
  letter-spacing: -0.02em;
  font-family: var(--font-primary, sans-serif);
`;

const ReadyScript = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: rgba(240, 253, 244, 0.85);
  margin: 0 0 1.25rem 0;
  font-family: var(--font-secondary, sans-serif);
  background: rgba(74, 222, 128, 0.06);
  border: 1px solid rgba(74, 222, 128, 0.15);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
`;

const PhoneMeta = styled.p`
  font-size: 0.88rem;
  line-height: 1.5;
  color: rgba(240, 253, 244, 0.5);
  margin: 0 0 2rem 0;
  font-family: var(--font-secondary, sans-serif);
`;

const PhoneInline = styled.span`
  color: #4ade80;
  font-weight: 600;
  font-family: var(--font-primary, monospace);
  letter-spacing: 0.03em;
`;

const AddressHighlight = styled.span`
  color: #4ade80;
  font-weight: 600;
`;

const WhatHappens = styled.div`
  text-align: left;
  margin-bottom: 2.5rem;
`;

const WhatHappensTitle = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(240, 253, 244, 0.45);
  margin: 0 0 1rem 0;
`;

const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const StepItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
`;

const StepNumber = styled.span`
  flex-shrink: 0;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  background: rgba(74, 222, 128, 0.15);
  border: 1px solid rgba(74, 222, 128, 0.3);
  color: #4ade80;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepText = styled.span`
  font-size: 0.95rem;
  line-height: 1.55;
  color: rgba(240, 253, 244, 0.7);
  font-family: var(--font-secondary, sans-serif);
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PrimaryAction = styled.button`
  padding: 0.95rem 2rem;
  font-size: 1rem;
  font-weight: 700;
  background: #16a34a;
  color: #f0fdf4;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: var(--font-primary, sans-serif);
  transition: background 0.2s, transform 0.1s;
  &:hover { background: #15803d; }
  &:active { transform: scale(0.98); }
`;

const SecondaryAction = styled.button`
  padding: 0.75rem 2rem;
  font-size: 0.95rem;
  font-weight: 500;
  background: transparent;
  color: rgba(240, 253, 244, 0.55);
  border: 1px solid rgba(240, 253, 244, 0.15);
  border-radius: 10px;
  cursor: pointer;
  font-family: var(--font-secondary, sans-serif);
  transition: border-color 0.2s, color 0.2s;
  &:hover {
    border-color: rgba(74, 222, 128, 0.4);
    color: rgba(240, 253, 244, 0.85);
  }
`;

/* Loading state */
const LoadingCard = styled.div`
  max-width: 480px;
  width: 100%;
  text-align: center;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid rgba(74, 222, 128, 0.2);
  border-top-color: #4ade80;
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
  margin: 0 auto 1.5rem;
`;

const LoadingTitle = styled.h2`
  font-size: 1.35rem;
  font-weight: 600;
  color: #f0fdf4;
  margin: 0 0 1.5rem 0;
  font-family: var(--font-primary, sans-serif);
`;

const LoadingSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Step = styled.p`
  font-size: 0.9rem;
  color: rgba(240, 253, 244, 0.55);
  margin: 0;
  animation: ${fadeStep} 3s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || "0s"};
  font-family: var(--font-secondary, sans-serif);
`;

/* Error state */
const ErrorCard = styled.div`
  max-width: 480px;
  width: 100%;
  text-align: center;
`;

const ErrorIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.12);
  border: 2px solid rgba(239, 68, 68, 0.4);
  color: #f87171;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
`;

const ErrorTitle = styled.h2`
  font-size: 1.35rem;
  font-weight: 600;
  color: #f0fdf4;
  margin: 0 0 0.75rem 0;
  font-family: var(--font-primary, sans-serif);
`;

const ErrorBody = styled.p`
  font-size: 0.95rem;
  color: rgba(240, 253, 244, 0.65);
  margin: 0 0 1.5rem 0;
  font-family: var(--font-secondary, sans-serif);
`;

const RetryButton = styled.button`
  padding: 0.75rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  background: rgba(239, 68, 68, 0.12);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.35);
  border-radius: 10px;
  cursor: pointer;
  font-family: var(--font-primary, sans-serif);
  transition: background 0.2s;
  &:hover { background: rgba(239, 68, 68, 0.2); }
`;
